import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { getApiErrorMessage } from "../utils/errorHandler";
import ReservationStripeModal from "@/components/ReservationStripePaymentModal";
import {
  CalendarDays,
  Clock3,
  Users,
  Phone,
  Mail,
  FileText,
  CheckCircle2,
  MapPin,
  UtensilsCrossed,
  Loader2,
  CreditCard,
  X,
} from "lucide-react";
import {
  Elements
} from "@stripe/react-stripe-js";

import {
  loadStripe
} from "@stripe/stripe-js";
const API = `${window.location.origin}/api`;

const formatDate = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleDateString(
    "en-IN",
    {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  );
};

const formatTime = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleTimeString(
    "en-IN",
    {
      hour: "numeric",
      minute: "2-digit",
    }
  );
};

const formatSlotTime = (time) => {
  const [hour, minute] = time.split(":");

  const date = new Date();

  date.setHours(hour);
  date.setMinutes(minute);

  return date.toLocaleTimeString(
    "en-IN",
    {
      hour: "numeric",
      minute: "2-digit",
    }
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case "confirmed":
      return "bg-green-100 text-green-700 border-green-200";

    case "pending":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";

    case "cancelled":
      return "bg-red-100 text-red-700 border-red-200";

    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const generateTimeSlots = (
  startTime,
  endTime,
  interval
) => {
  const slots = [];

  const start = new Date();
  const end = new Date();

  const [startHour, startMinute] =
    startTime.split(":");

  const [endHour, endMinute] =
    endTime.split(":");

  start.setHours(
    startHour,
    startMinute,
    0
  );

  end.setHours(
    endHour,
    endMinute,
    0
  );

  while (start < end) {
    slots.push(
      start.toTimeString().slice(0, 5)
    );

    start.setMinutes(
      start.getMinutes() + interval
    );
  }

  return slots;
};

export default function ReservationBookingPage() {
  const { restaurantSlug } =
    useParams();

  const [restaurant, setRestaurant] =
    useState(null);

  const [reservationEnabled, setReservationEnabled] =
    useState(true);

  const [settings, setSettings] =
    useState(null);

  const [sections, setSections] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [reservation, setReservation] =
    useState(null);

  const [selectedDate, setSelectedDate] =
    useState("");

  const [availableSlots, setAvailableSlots] =
    useState([]);

  const [formData, setFormData] =
    useState({
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      party_size: 2,
      reservation_date: "",
      reservation_time: "",
      notes: "",
      preferred_section_id: "",
    });

  // Payment related states
  const [showPaymentModal, setShowPaymentModal] =
    useState(false);

// Payment related states

const [clientSecret, setClientSecret] =
  useState("");

const [paymentIntent, setPaymentIntent] =
  useState(null);
  const [restaurantSuspended, setRestaurantSuspended] =
  useState(false);

const [creatingIntent, setCreatingIntent] =
  useState(false);
const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    fetchRestaurant();
  }, [restaurantSlug]);

  useEffect(() => {
    if (
      !selectedDate ||
      !settings
    ) {
      setAvailableSlots([]);
      return;
    }

    const date = new Date(
      selectedDate
    );

    const dayName = date
      .toLocaleDateString(
        "en-US",
        {
          weekday: "long",
        }
      )
      .toLowerCase();

    const dayConfig =
      settings.reservation_hours.find(
        (day) =>
          day.day === dayName
      );

    if (
      !dayConfig ||
      !dayConfig.enabled
    ) {
      setAvailableSlots([]);
      return;
    }

    const slots =
      generateTimeSlots(
        dayConfig.start_time,
        dayConfig.end_time,
        settings.slot_duration_minutes
      );

    const now = new Date();

    const filteredSlots =
      slots.filter((slot) => {
        const slotDate =
          new Date(
            `${selectedDate}T${slot}:00`
          );

        return slotDate > now;
      });

    setAvailableSlots(
      filteredSlots
    );
  }, [
    selectedDate,
    settings,
  ]);

  const fetchRestaurant =
    async () => {
      try {
        setLoading(true);

        const response =
          await axios.get(
            `${API}/public/restaurant/${restaurantSlug}`
          );
          if (
            response.data?.status === "suspended"
          ) {
            setRestaurantSuspended(true);
            return;
          }
        setRestaurant(
          response.data
        );

        // Check if reservations are enabled
        setReservationEnabled(
          response.data?.reservation_enabled ?? true
        );

        await Promise.all([
          fetchReservationSettings(
            response.data.id
          ),

          fetchSections(
            response.data.id
          ),
        ]);
      } catch (error) {
        console.error(error);

        toast.error(
          "Restaurant not found"
        );
      } finally {
        setLoading(false);
      }
    };

  const fetchReservationSettings =
    async (restaurantId) => {
      try {
        const response =
          await axios.get(
            `${API}/public/reservations/settings`,
            {
              params: {
                restaurant_id:
                  restaurantId,
              },
            }
          );

        setSettings(
          response.data
        );
      } catch (error) {
        console.error(error);

        toast.error(
          "Failed to load reservation settings"
        );
      }
    };

  const fetchSections =
    async (restaurantId) => {
      try {
        const response =
          await axios.get(
            `${API}/public/tables`,
            {
              params: {
                restaurant_id:
                  restaurantId,
              },
            }
          );

        setSections(
          response.data.data || []
        );
      } catch (error) {
        console.error(error);

        toast.error(
          "Failed to load sections"
        );
      }
    };

  const handleChange = (
    field,
    value
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

const createReservation = async () => {
  try {
    if (
      !formData.customer_name ||
      !formData.customer_phone ||
      !formData.reservation_date ||
      !formData.reservation_time
    ) {
      toast.error(
        "Please fill all required fields"
      );

      return;
    }

    const reservationDateTime =
      `${formData.reservation_date}T${formData.reservation_time}:00`;

    const payload = {
      customer_name:
        formData.customer_name,

      customer_phone:
        formData.customer_phone,

      customer_email:
        formData.customer_email,

      party_size: Number(
        formData.party_size
      ),

      restaurant_id:
        restaurant.id,

      reservation_date:
        reservationDateTime,

      notes:
        formData.notes,

      preferred_section_id:
        formData.preferred_section_id,
    };
    const hasDepositAmount =
      settings?.reservation_deposit_amount &&
      parseFloat(settings.reservation_deposit_amount) > 0;

    const paymentEnabled =
      settings?.reservation_payment_enabled;

    if (!paymentEnabled || !hasDepositAmount) {
    // if (true) {
    await submitReservation();
      return;
    }

    // CREATE PAYMENT INTENT
    setCreatingIntent(true);

    const response =
      await axios.post(
        `${API}/payments/reservations/create-intent`,
        payload
      );

    setClientSecret(
      response.data.client_secret
    );

    setPaymentIntent(
      response.data
    );
    // Initialize Stripe with publishable key from API response
    const stripe = await loadStripe(response.data.publishable_key);
    setStripePromise(stripe);
    setShowPaymentModal(true);

  } catch (error) {
    console.error(error);

    toast.error(
      getApiErrorMessage(error)
    );
  } finally {
    setCreatingIntent(false);
  }
};

  const submitReservation =
    async (paymentId = null) => {
      try {
        setSubmitting(true);

        const reservationDateTime =
          `${formData.reservation_date}T${formData.reservation_time}:00`;

        const payload = {
          customer_name:
            formData.customer_name,

          customer_phone:
            formData.customer_phone,

          customer_email:
            formData.customer_email,

          party_size: Number(
            formData.party_size
          ),

          restaurant_id:
            restaurant.id,

          reservation_date:
            reservationDateTime,

          notes:
            formData.notes,

          preferred_section_id:
            formData.preferred_section_id,
        };

        // Add payment info if exists
        if (paymentId) {
          payload.payment_id =
            paymentId;

          payload.payment_method =
            "stripe";

          payload.payment_status =
            "completed";
        }

        const response =
          await axios.post(
            `${API}/public/reservations`,
            payload
          );

        setReservation(
          response.data
        );

        toast.success(
          response.data.status ===
            "confirmed"
            ? "Reservation confirmed successfully"
            : "Reservation submitted successfully"
        );
      } catch (error) {
        console.error(error);

        toast.error(
          getApiErrorMessage(
            error
          )
        );
      } finally {
        setSubmitting(false);
      }
    };
if (restaurantSuspended) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-md text-center p-6">
        <h1 className="text-2xl font-bold">
          Restaurant Unavailable
        </h1>

        <p className="mt-2 text-gray-500">
          This restaurant is currently unavailable.
        </p>
      </div>
    </div>
  );
}
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-700">
          <Loader2
            className="animate-spin"
            size={24}
          />

          Loading restaurant...
        </div>
      </div>
    );
  }

  if (reservation) {
    const isConfirmed = reservation.status === "confirmed";
    
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-6 flex items-center justify-center">
        <div className="w-full max-w-md bg-white rounded-[28px] shadow-sm border p-6 text-center">

          {/* SUCCESS ICON */}
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${
            isConfirmed 
              ? "bg-green-100" 
              : "bg-blue-100"
          }`}>
            <CheckCircle2
              className={isConfirmed ? "text-green-600" : "text-blue-600"}
              size={42}
            />
          </div>

          {/* TITLE & SUBTITLE */}
          <h1 className="text-2xl font-black text-slate-900 mb-2">
            {isConfirmed 
              ? "Reservation Confirmed" 
              : "Reservation Submitted"}
          </h1>

          <p className="text-slate-500 mb-6">
            {isConfirmed
              ? "Your reservation has been booked successfully"
              : "Your reservation request has been received. The restaurant will contact you shortly to confirm your booking."}
          </p>

          {/* RESERVATION DETAILS */}
          <div className="border rounded-3xl p-5 space-y-4 text-left">

            <div className="flex justify-between">
              <span className="text-slate-500">
                Reservation No
              </span>

              <span className="font-bold">
                {
                  reservation.reservation_number
                }
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-500">
                Status
              </span>

              <span
                className={`px-3 py-1 rounded-full border text-xs font-bold ${getStatusColor(
                  reservation.status
                )}`}
              >
                {
                  reservation.status
                }
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-500">
                Date
              </span>

              <span className="font-bold">
                {formatDate(
                  reservation.reservation_date
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-500">
                Time
              </span>

              <span className="font-bold">
                {formatTime(
                  reservation.reservation_date
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-500">
                Guests
              </span>

              <span className="font-bold">
                {
                  reservation.party_size
                }
              </span>
            </div>

            {reservation.section_names
              ?.length > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500">
                  Section
                </span>

                <span className="font-bold">
                  {reservation.section_names.join(
                    ", "
                  )}
                </span>
              </div>
            )}

            {/* {reservation.table_names
              ?.length > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500">
                  Table
                </span>

                <span className="font-bold">
                  {reservation.table_names.join(
                    ", "
                  )}
                </span>
              </div>
            )} */}
          </div>

          {/* INFO BOX FOR PENDING RESERVATIONS */}
          {!isConfirmed && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-4 text-left">
              <p className="text-sm text-blue-900">
                <span className="font-bold">📞 Next Steps:</span>
              </p>
              <p className="text-sm text-blue-800 mt-2">
                The restaurant will review your reservation and contact you at <strong>{reservation.customer_phone}</strong> or <strong>{reservation.customer_email}</strong> to confirm.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show modal if reservations are not enabled
  if (!reservationEnabled) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-6 flex items-center justify-center">
        <div className="w-full max-w-md bg-white rounded-[28px] shadow-sm border p-6 text-center">
          
          {/* UNAVAILABLE ICON */}
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 bg-red-100">
            <span className="text-4xl">🚫</span>
          </div>

          {/* TITLE & SUBTITLE */}
          <h1 className="text-2xl font-black text-slate-900 mb-2">
            Reservations Unavailable
          </h1>

          <p className="text-slate-600 mb-6">
            We're sorry, but reservations are currently not available at <strong>{restaurant?.name}</strong>.
          </p>

          {/* INFO BOX */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 text-left mb-6">
            <p className="text-sm text-yellow-900">
              <span className="font-bold">ℹ️ Please note:</span>
            </p>
            <p className="text-sm text-yellow-800 mt-2">
              The restaurant is not accepting reservations through this system at the moment. Please contact them directly for dining arrangements.
            </p>
          </div>

          {/* CONTACT INFO */}
          {restaurant?.phone && (
            <div className="border-t pt-4">
              <p className="text-sm text-slate-600 mb-2">Contact the restaurant:</p>
              <p className="text-lg font-bold text-slate-900">{restaurant.phone}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-slate-100 py-4 md:py-10 px-3 md:px-4">

        <div className="max-w-7xl mx-auto">

        {/* HEADER */}

        <div className="bg-white rounded-[32px] shadow-sm p-5 md:p-8 mb-6 border">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

            <div>

              <div className="flex items-center gap-4 mb-4">

                <div className="bg-black text-white p-4 rounded-3xl">
                  <UtensilsCrossed size={28} />
                </div>

                <div>

                  <h1 className="text-3xl font-black text-slate-900">
                    {restaurant?.name}
                  </h1>

                  <p className="text-slate-500 mt-1">
                    Reserve Your Dining Experience
                  </p>

                </div>

              </div>

              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <MapPin size={16} />
                {restaurant?.address}
              </div>

            </div>

          </div>

        </div>

        <div className="grid lg:grid-cols-3 gap-6">

          {/* FORM */}

          <div className="lg:col-span-2">

            <div className="bg-white rounded-[32px] shadow-sm border p-5 md:p-8">

              <div className="space-y-8">

                {/* DATE */}

                <div>

                  <label className="text-sm font-bold text-slate-700 mb-4 block">
                    Select Reservation Date
                  </label>

                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3">

                    {[...Array(7)].map(
                      (_, index) => {

                        const date =
                          new Date();

                        date.setDate(
                          date.getDate() +
                            index
                        );

                        const formattedDate =
                          `${date.getFullYear()}-${String(
                            date.getMonth() + 1
                          ).padStart(
                            2,
                            "0"
                          )}-${String(
                            date.getDate()
                          ).padStart(
                            2,
                            "0"
                          )}`;

                        const dayName =
                          date
                            .toLocaleDateString(
                              "en-US",
                              {
                                weekday:
                                  "long",
                              }
                            )
                            .toLowerCase();

                        const isAvailable =
                          settings?.reservation_hours?.find(
                            (d) =>
                              d.day ===
                                dayName &&
                              d.enabled
                          );

                        return (
                          <button
                            key={
                              formattedDate
                            }
                            type="button"
                            disabled={
                              !isAvailable
                            }
                            onClick={() => {

                              setSelectedDate(
                                formattedDate
                              );

                              handleChange(
                                "reservation_date",
                                formattedDate
                              );

                              handleChange(
                                "reservation_time",
                                ""
                              );
                            }}
                            className={`rounded-2xl border p-4 text-left transition ${
                              selectedDate ===
                              formattedDate
                                ? "bg-black text-white border-black"
                                : "bg-white hover:border-black"
                            } ${
                              !isAvailable
                                ? "opacity-40 cursor-not-allowed"
                                : ""
                            }`}
                          >

                            <p className="text-xs uppercase">
                              {date.toLocaleDateString(
                                "en-IN",
                                {
                                  weekday:
                                    "short",
                                }
                              )}
                            </p>

                            <p className="text-3xl font-black mt-2">
                              {date.getDate()}
                            </p>

                            <p className="text-xs mt-2">
                              {date.toLocaleDateString(
                                "en-IN",
                                {
                                  month:
                                    "short",
                                }
                              )}
                            </p>

                          </button>
                        );
                      }
                    )}

                  </div>

                </div>

                {/* SLOTS */}

                <div>

                  <label className="text-sm font-bold text-slate-700 mb-4 block">
                    Available Time Slots
                  </label>

                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">

                    {availableSlots.map(
                      (slot) => {

                        const selected =
                          formData.reservation_time ===
                          slot;

                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() =>
                              handleChange(
                                "reservation_time",
                                slot
                              )
                            }
                            className={`rounded-2xl border py-4 px-4 transition font-bold flex items-center justify-center gap-2 ${
                              selected
                                ? "bg-black text-white border-black"
                                : "bg-white hover:border-black"
                            }`}
                          >

                            <Clock3 size={16} />

                            {formatSlotTime(
                              slot
                            )}

                          </button>
                        );
                      }
                    )}

                  </div>

                </div>

                {/* SECTION */}

                <div>

                  <label className="text-sm font-bold text-slate-700 mb-4 block">
                    Preferred Seating Area
                  </label>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">

                    <button
                      type="button"
                      onClick={() =>
                        handleChange(
                          "preferred_section_id",
                          ""
                        )
                      }
                      className={`border rounded-2xl p-4 text-left transition ${
                        !formData.preferred_section_id
                          ? "bg-black text-white border-black"
                          : "bg-white hover:border-black"
                      }`}
                    >

                      <p className="font-bold">
                        Any Section
                      </p>

                      <p className="text-xs opacity-70 mt-1">
                        Auto assign best table
                      </p>

                    </button>

                    {sections.map(
                      (section) => (

                        <button
                          key={
                            section.id
                          }
                          type="button"
                          onClick={() =>
                            handleChange(
                              "preferred_section_id",
                              section.id
                            )
                          }
                          className={`border rounded-2xl p-4 text-left transition ${
                            formData.preferred_section_id ===
                            section.id
                              ? "bg-black text-white border-black"
                              : "bg-white hover:border-black"
                          }`}
                        >

                          <div className="flex items-center gap-2">

                            <div
                              className="w-3 h-3 rounded-full"
                              style={{
                                background:
                                  section.color ||
                                  "gray",
                              }}
                            />

                            <p className="font-bold">
                              {
                                section.name
                              }
                            </p>

                          </div>

                          <p className="text-xs opacity-70 mt-2">
                            {
                              section.table_count
                            }{" "}
                            tables
                          </p>

                        </button>
                      )
                    )}

                  </div>

                </div>

                {/* CUSTOMER */}

                <div className="grid md:grid-cols-2 gap-5">

                  <input
                    placeholder="Full Name"
                    value={
                      formData.customer_name
                    }
                    onChange={(e) =>
                      handleChange(
                        "customer_name",
                        e.target.value
                      )
                    }
                    className="border rounded-2xl px-4 py-4"
                  />

                  <input
                    placeholder="Phone Number"
                    value={
                      formData.customer_phone
                    }
                    onChange={(e) =>
                      handleChange(
                        "customer_phone",
                        e.target.value
                      )
                    }
                    className="border rounded-2xl px-4 py-4"
                  />

                  <input
                    placeholder="Email Address"
                    value={
                      formData.customer_email
                    }
                    onChange={(e) =>
                      handleChange(
                        "customer_email",
                        e.target.value
                      )
                    }
                    className="border rounded-2xl px-4 py-4"
                  />

                  <input
                    type="number"
                    min={1}
                    value={
                      formData.party_size
                    }
                    onChange={(e) =>
                      handleChange(
                        "party_size",
                        e.target.value
                      )
                    }
                    className="border rounded-2xl px-4 py-4"
                  />

                  <textarea
                    rows={3}
                    placeholder="Special notes"
                    value={
                      formData.notes
                    }
                    onChange={(e) =>
                      handleChange(
                        "notes",
                        e.target.value
                      )
                    }
                    className="md:col-span-2 border rounded-2xl px-4 py-4 resize-none"
                  />

                </div>

                {/* SUBMIT */}

                <button
                  onClick={
                    createReservation
                  }
                  disabled={
                    submitting
                  }
                  className="w-full bg-black text-white py-5 rounded-3xl font-bold flex items-center justify-center gap-3"
                >

                  {submitting ? (
                    <>
                      <Loader2
                        className="animate-spin"
                        size={22}
                      />

                      Creating Reservation...
                    </>
                  ) : (
                    <>
                      <CalendarDays
                        size={22}
                      />

                      Confirm Reservation
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>

          {/* SUMMARY */}

          <div>

            <div className="bg-white rounded-[32px] shadow-sm border p-6 sticky top-6">

              <h3 className="text-2xl font-black text-slate-900 mb-6">
                Reservation Summary
              </h3>

              <div className="space-y-5">

                <div className="border rounded-3xl p-5">

                  <div className="space-y-4">

                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        Date
                      </span>

                      <span className="font-bold">
                        {formData.reservation_date
                          ? formatDate(
                              formData.reservation_date
                            )
                          : "-"}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        Time
                      </span>

                      <span className="font-bold">
                        {formData.reservation_time
                          ? formatSlotTime(
                              formData.reservation_time
                            )
                          : "-"}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        Guests
                      </span>

                      <span className="font-bold">
                        {
                          formData.party_size
                        }
                      </span>
                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>

      </div>

      {/* PAYMENT MODAL */}
     {showPaymentModal &&
  clientSecret && (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret
      }}
    >
      <ReservationStripeModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
        }}
        paymentIntent={paymentIntent}
        submitReservation={submitReservation}
        restaurant={restaurant}
        reservationData={{
          ...formData,
          reservation_date:
            `${formData.reservation_date}T${formData.reservation_time}:00`
        }}
      />
    </Elements>
)}
    </>
  );
}